/**
 * PATH: packages/ui/src/kanban/components/lane/LaneHeader.tsx
 * FILE_ID: UI-KANBAN-LANE-HEADER-001
 * PURPOSE: Lane title, count, and the overflow menu that searches this lane.
 * ROLE IN ARCHITECTURE: Presentational header under KanbanLane.
 * MAIN EXPORTS: LaneHeader
 * NON-RESPONSIBILITIES: Does not filter the card list. The view-model does.
 */
import { useEffect, useRef } from 'react'
import { Archive, ArrowDownUp, ArrowUpRight, Check, ChevronLeft, ChevronRight, ChevronsLeft, Copy, Inbox as InboxIcon, MoreHorizontal, Palette, Pencil } from 'lucide-react'
import { getLaneColor, getLaneKindLabel } from '../../utils'
import { KanbanCard } from '../KanbanCard'
import { LANE_COLOR_PALETTE, laneDisplayCount } from './constants'
import type { Lane } from '../../types'
import type { KanbanLaneProps } from './types'
import type { LaneVM } from './useKanbanLane'

export function LaneHeader({ props, vm }: { props: KanbanLaneProps; vm: LaneVM }) {
  const {
    lane,
    cards,
    onLaneSettings,
    headerSlot,
    onRename,
    onArchive,
    onSort,
    onMoveLane,
    canMoveLeft,
    canMoveRight,
    onToggleCollapse,
    onSetColor,
    onSetDescription,
    onRequestEditDescription,
    onCopyToBoard,
    onPromoteToBoard,
    onSetDisplayMode,
  } = props
  const { colorPickerOpen, setColorPickerOpen, renaming, setRenaming, draftName, setDraftName, menuOpen, setMenuOpen, renameRef, menuRef, overWipLimit, commitRename, laneSearch, setLaneSearch, searchedCount } = vm
  const laneSearchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (menuOpen) laneSearchRef.current?.focus()
  }, [menuOpen])
  return (
    <>
      {headerSlot ?? (
        <div
          // Stage-1 visual lift (docs/plans/KANBAN_VISUAL_LIFT_PLAN.md):
          // the lane colour moves from a muddy ~16% full-header wash to a
          // crisp solid colour *tag* beside the name (left group below) —
          // a colour pick is now MORE obvious than the old wash, without
          // tinting the whole header or fighting the themed --lane-fg
          // text contrast (BUG-2026-05-17). The count also moves out of
          // the title into its own muted chip so the name reads first.
          className="flex items-center justify-between gap-1.5 rounded-t-xl border-b border-neutral-200/70 dark:border-white/10 px-2.5 py-2"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Lane-colour tag — the single, unmistakable home for the
                lane's colour now that the full-header wash is gone. A
                solid vertical pill reads cleaner than the old 1.5px dot
                and stands out on light AND themed lane backgrounds. */}
            <span
              className="inline-block h-4 w-1.5 rounded-full shrink-0"
              style={{ background: getLaneColor(lane) }}
              aria-hidden="true"
            />
            {renaming && onRename ? (
              <input
                ref={renameRef}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void commitRename()
                  }
                  if (e.key === 'Escape') {
                    setDraftName(lane.name)
                    setRenaming(false)
                  }
                }}
                className="min-w-0 flex-1 rounded bg-white dark:bg-neutral-900 px-1 py-0.5 text-[12.5px] font-semibold text-neutral-800 dark:text-neutral-100 border border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
              />
            ) : (
              <button
                type="button"
                onClick={() => onRename && setRenaming(true)}
                disabled={!onRename}
                title={onRename ? `${getLaneKindLabel(lane.kind)} — click to rename` : getLaneKindLabel(lane.kind)}
                // BUG-2026-05-17-KANBAN-THEMED-LANE-TITLE-INVISIBLE —
                // when the parent <KanbanBoardView> publishes a
                // `--lane-fg` (every JOB / TRELLO_MIRROR board does
                // via getBoardTheme()'s auto-palette), use it. The
                // `, currentColor` fallback inherits the wrapper's
                // text-neutral-800/dark:text-neutral-100 when no theme
                // is in scope. Mirrors what KanbanCard already does
                // for `--card-fg` (qf!2926).
                style={{ color: 'var(--lane-fg, currentColor)' }}
                className={[
                  'min-w-0 flex-1 truncate text-left text-[12.5px] font-semibold',
                  // BUG-2026-05-21-KANBAN-LANE-DRAG-STEALS-CARD-DRAG —
                  // the lane title is the Trello-style drag handle for
                  // lane reorder (see KanbanBoardView lane wrapper
                  // gating). `cursor-grab` is the conventional signal
                  // that this element initiates a drag; `active:cursor-
                  // grabbing` gives feedback during the pull. Inline
                  // rename is still a click — single-click on this
                  // button opens the rename input.
                  'cursor-grab active:cursor-grabbing',
                  onRename ? 'rounded px-1 -mx-1 py-0.5 hover:bg-neutral-200/60 dark:hover:bg-zinc-800/80' : '',
                ].join(' ')}
                data-testid="kanban-lane-title"
              >
                {lane.name}
                {lane.is_default_landing && (
                  <span
                    className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide align-middle"
                    title="Default landing lane — AI-categorised emails for this board land here"
                    aria-label="Default landing lane"
                    data-testid="kanban-lane-default-landing-badge"
                  >
                    <InboxIcon className="h-2.5 w-2.5" aria-hidden />
                    AI
                  </span>
                )}
                {lane.is_primary_inbox && (
                  <span
                    className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide align-middle"
                    title="Primary inbox lane — captures and inbox filters use this lane"
                    aria-label="Primary inbox lane"
                    data-testid="kanban-lane-primary-inbox-badge"
                  >
                    <InboxIcon className="h-2.5 w-2.5" aria-hidden />
                    Inbox
                  </span>
                )}
              </button>
            )}
            {!renaming && (
              // Count chip — lifted OUT of the title button (Stage 1) into a
              // standalone muted pill so the lane name reads first and the
              // count stops competing with it. Mirrors the collapsed-lane
              // chip. Themed boards tint it via --lane-fg-muted (themed.ts
              // emits ~70% alpha of --lane-fg); the WIP-over-limit breach
              // keeps its uncontested red and intentionally does NOT take
              // the muted override (BUG-2026-05-17 contrast contract).
              <span
                data-testid="kanban-lane-count"
                className={[
                  'shrink-0 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                  overWipLimit
                    ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                    : 'bg-neutral-200/70 dark:bg-zinc-800 text-neutral-500 dark:text-zinc-400',
                ].join(' ')}
                style={
                  overWipLimit
                    ? undefined
                    : { color: 'var(--lane-fg-muted, currentColor)' }
                }
                title={lane.wip_limit > 0 ? `WIP limit: ${lane.wip_limit}` : undefined}
              >
                {/* BUG-2026-07-06-KANBAN-LANE-COUNT-CAPPED: when the per-lane
                    fetch hit the server hard cap, `cards.length` IS the cap
                    (operator saw "2000" on an 11,813-email Triage). Prefer
                    the server-annotated lane total in that case. */}
                {(laneSearch.trim() ? searchedCount : laneDisplayCount(lane.card_count, cards.length)).toLocaleString()}{lane.wip_limit > 0 ? `/${lane.wip_limit}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 relative shrink-0">
            {/* Lane reorder arrows. Trello / Planner pattern — the
                operator can nudge the lane one slot left or right
                without picking up + dragging the whole header. The
                parent passes canMoveLeft/Right so we don't need to
                know the surrounding lane order. */}
            {/* qf!Newmark-board-density — reorder + collapse chevrons
                are decorative chrome that compete with the lane title
                at rest. On desktop (md+) they fade in only while the
                lane is being hovered (`group-hover/lane`) or has
                keyboard focus inside it (`group-focus-within/lane`).
                On mobile they stay always visible — there's no hover
                state, and the long-press context menu already handles
                lane operations there. Reasoning: matches the personal
                Trello/Benko board the operator referenced as the
                visual target; reduces header noise so the title +
                count read first. The `...` overflow menu still
                exposes Move/Collapse for keyboard / discoverability,
                so this is a pure visual tweak — no functionality is
                gated behind hover. */}
            {onMoveLane && (
              <>
                <button
                  type="button"
                  disabled={canMoveLeft === false}
                  onClick={() => void onMoveLane(lane, 'left')}
                  aria-label="Move list left"
                  title="Move list left"
                  className="rounded p-0.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-transparent md:opacity-0 md:group-hover/lane:opacity-100 md:group-focus-within/lane:opacity-100 md:focus-visible:opacity-100 md:transition-opacity md:duration-150"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={canMoveRight === false}
                  onClick={() => void onMoveLane(lane, 'right')}
                  aria-label="Move list right"
                  title="Move list right"
                  className="rounded p-0.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:hover:bg-transparent md:opacity-0 md:group-hover/lane:opacity-100 md:group-focus-within/lane:opacity-100 md:focus-visible:opacity-100 md:transition-opacity md:duration-150"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {onToggleCollapse && (
              <button
                type="button"
                onClick={() => onToggleCollapse(lane)}
                aria-label="Collapse list"
                title="Collapse list"
                className="rounded p-0.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 dark:hover:bg-zinc-800 md:opacity-0 md:group-hover/lane:opacity-100 md:group-focus-within/lane:opacity-100 md:focus-visible:opacity-100 md:transition-opacity md:duration-150"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
            )}
              <div ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label="Lane menu"
                  className="rounded p-0.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200 dark:hover:bg-zinc-800"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-7 z-30 w-56 overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-lg">
                    <div className="border-b border-neutral-200 p-2 dark:border-neutral-800">
                      <input
                        ref={laneSearchRef}
                        type="search"
                        value={laneSearch}
                        data-testid="kanban-lane-menu-search"
                        aria-label="Search this lane"
                        placeholder="Search this lane…"
                        onChange={(e) => setLaneSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="h-7 w-full rounded border border-neutral-200 bg-white px-2 text-xs text-neutral-800 placeholder:text-neutral-400 focus:border-blue-400 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                      />
                    </div>
                    {onRename && (
                      <button
                        type="button"
                        onClick={() => {
                          setRenaming(true)
                          setMenuOpen(false)
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                      >
                        <Pencil className="h-3 w-3" /> Rename list
                      </button>
                    )}
                    {onMoveLane && (
                      <>
                        <button
                          type="button"
                          disabled={canMoveLeft === false}
                          onClick={() => {
                            void onMoveLane(lane, 'left')
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <ChevronLeft className="h-3 w-3" /> Move list left
                        </button>
                        <button
                          type="button"
                          disabled={canMoveRight === false}
                          onClick={() => {
                            void onMoveLane(lane, 'right')
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900 disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <ChevronRight className="h-3 w-3" /> Move list right
                        </button>
                      </>
                    )}
                    {onSort && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800 px-3 py-1 text-[10px] uppercase tracking-wide text-neutral-400">
                          Sort by
                        </div>
                        {(['title', 'due', 'priority', 'updated'] as const).map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => {
                              onSort(lane, k)
                              setMenuOpen(false)
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                          >
                            <ArrowDownUp className="h-3 w-3" />
                            {k === 'title' && 'Title (A-Z)'}
                            {k === 'due' && 'Due date'}
                            {k === 'priority' && 'Priority'}
                            {k === 'updated' && 'Last updated'}
                          </button>
                        ))}
                      </>
                    )}
                    {onSetColor && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800" />
                        <button
                          type="button"
                          onClick={() => setColorPickerOpen((o) => !o)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Palette className="h-3 w-3" /> Color…
                          </span>
                          <span
                            className="inline-block w-3 h-3 rounded border border-neutral-300 dark:border-zinc-700"
                            style={{ background: getLaneColor(lane) }}
                          />
                        </button>
                        {colorPickerOpen && (
                          <div className="border-t border-neutral-200 dark:border-neutral-800 p-2">
                            <div className="grid grid-cols-7 gap-1.5">
                              {LANE_COLOR_PALETTE.map((hex) => {
                                const active = (lane.color ?? '').toLowerCase() === hex.toLowerCase()
                                return (
                                  <button
                                    key={hex}
                                    type="button"
                                    onClick={() => {
                                      void onSetColor(lane, hex)
                                      setColorPickerOpen(false)
                                      setMenuOpen(false)
                                    }}
                                    aria-label={`Set lane color to ${hex}`}
                                    title={hex}
                                    className={[
                                      'h-5 w-5 rounded border transition-transform hover:scale-110',
                                      active ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-zinc-950' : 'border-neutral-300 dark:border-zinc-700',
                                    ].join(' ')}
                                    style={{ background: hex }}
                                  />
                                )
                              })}
                              <button
                                type="button"
                                onClick={() => {
                                  void onSetColor(lane, '')
                                  setColorPickerOpen(false)
                                  setMenuOpen(false)
                                }}
                                aria-label="Clear lane color (use default)"
                                title="Clear (use default)"
                                className="h-5 w-5 rounded border border-neutral-300 bg-white text-[9px] text-neutral-500 hover:bg-neutral-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {(onRequestEditDescription || onSetDescription) && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800" />
                        <button
                          type="button"
                          onClick={(e) => {
                            if (onRequestEditDescription) {
                              const r = e.currentTarget.getBoundingClientRect()
                              onRequestEditDescription(lane, {
                                x: r.left,
                                y: r.bottom + 4,
                              })
                            } else if (onSetDescription) {
                              const next = window.prompt(
                                `Edit description for "${lane.name}":`,
                                lane.description ?? '',
                              )
                              if (next === null) return
                              void onSetDescription(lane, next.trim())
                            }
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                        >
                          <Pencil className="h-3 w-3" /> Edit description…
                        </button>
                      </>
                    )}
                    {onSetDisplayMode && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800" />
                        <button
                          type="button"
                          onClick={() => {
                            const next = lane.display_mode === 'checklist' ? 'kanban' : 'checklist'
                            void onSetDisplayMode(lane, next)
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                          title={
                            lane.display_mode === 'checklist'
                              ? 'Switch back to the default tile layout'
                              : 'Render cards as flat tick / archive rows'
                          }
                        >
                          {lane.display_mode === 'checklist' ? (
                            <>
                              <ArrowDownUp className="h-3 w-3" /> Switch to kanban mode
                            </>
                          ) : (
                            <>
                              <Check className="h-3 w-3" /> Switch to checklist mode
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {onCopyToBoard && (
                      <button
                        type="button"
                        onClick={() => {
                          onCopyToBoard(lane)
                          setMenuOpen(false)
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                      >
                        <Copy className="h-3 w-3" /> Copy to another board…
                      </button>
                    )}
                    {onPromoteToBoard && (
                      <button
                        type="button"
                        data-testid="kanban-lane-promote-sub-board"
                        onClick={() => {
                          onPromoteToBoard(lane)
                          setMenuOpen(false)
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                      >
                        <ArrowUpRight className="h-3 w-3" /> Promote to sub-board…
                      </button>
                    )}
                    {onArchive && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800" />
                        <button
                          type="button"
                          onClick={() => {
                            void onArchive(lane)
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                        >
                          <Archive className="h-3 w-3" /> Archive list
                        </button>
                      </>
                    )}
                    {onLaneSettings && (
                      <>
                        <div className="border-t border-neutral-200 dark:border-neutral-800" />
                        <button
                          type="button"
                          onClick={() => {
                            onLaneSettings(lane)
                            setMenuOpen(false)
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                        >
                          <MoreHorizontal className="h-3 w-3" /> Settings…
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
          </div>
        </div>
      )}
    </>
  )
}
